import type { AnnotationsProps } from '@repo/image-annotator/lib';
import type { Locale } from 'date-fns';
import { format } from 'date-fns';
import { atom } from 'jotai';

import { parseAndFormatDate } from '@/utils/parseDate';

import { useGetDateFNSLocale } from '../../../../locales/helpers';
import type { Appointment } from '../../../../react-query/useAppointments';
import type { InsuranceView } from '../../../insuranceExport/constants';
import { getProviderName } from '../../../patientList/huddlePrintoutContent/helpers';
import { getDisplayName } from '../../helpers';
import type { TGetNarrativeDev } from '../narrativeDevTool/useGetGptResponse';

export const formatDate = (
  date: string | undefined,
  locale?: Locale,
): string => {
  if (!date) return '';
  try {
    return format(new Date(date), 'P', { locale });
  } catch (error) {
    // If date parsing fails, return the original string as it might be already formatted
    return date;
  }
};

export const useLocalizedFormatDate = () => {
  const locale = useGetDateFNSLocale();
  return (
    date: string | undefined,
    useUTConversion: boolean = false,
    // Pass true to preserve date as-is without converting to local timezone (e.g., for simple string date like "1990-10-01").
    // Pass false/undefined for UTC timestamps that should be displayed in user's local time.
    formatString: string = 'P',
  ): string => {
    if (!date) return '';
    try {
      if (useUTConversion) {
        return parseAndFormatDate(date, formatString, locale);
      }
      return format(new Date(date), formatString, { locale });
    } catch (error) {
      // If date parsing fails, return the original string as it might be already formatted
      return date;
    }
  };
};

export type TIncludeTypes = {
  includeCariousLesions: boolean;
  includePeriapicalRadiolucency: boolean;
  includeRadiographicBoneLoss: boolean;
  includeDmf: boolean;
  includeRadiographsTakenDate: boolean;
  includeBitewingsOnly: boolean;
};

export type TPatientInfo = {
  name: string;
  dob: string;
  dateOfVisit: string;
  radiographsTakenDate: string | undefined;
  providerName: string;
};

export type TView = 'appointment' | 'image';

export const claimSummaryViewAtom = atom<TView>('appointment');
export const insPagesAtom = atom<InsuranceView>('config');
export const claimSummaryUnifiedIdAtom = atom<string>('');
export const claimSummaryEstimatedVisitIdAtom = atom<string | undefined>(
  undefined,
);
export const claimSummaryImagingPatientIdAtom = atom<string | undefined>(
  undefined,
);
export const appointmentPmsAppointmentIdAtom = atom<string | undefined>(
  undefined,
);
export const patientNameAtom = atom<string>('');
export const patientDOBAtom = atom<string>('');
export const dateOfVisitAtom = atom<string>('');
export const radiographsTakenDateAtom = atom<string | undefined>(undefined);
export const appointmentStartAtom = atom<string | undefined>(undefined);
export const providerNameAtom = atom<string>('');
export const narrativeAtom = atom<string>('');
export const isNarrativeLoadingAtom = atom<boolean>(false);
export const narrativeDevToolDataAtom = atom<TGetNarrativeDev | undefined>(
  undefined,
);
export const narrativeTraceIdAtom = atom<string | undefined>(undefined);
export const selectedTeethAtom = atom<string[]>([]);
export const defaultSelectedTeethAtom = atom<string[] | null>([]);
export const teethInImagesAtom = atom<string[]>([]);

export const selectTeethAtom = atom(
  (get) => get(selectedTeethAtom) ?? [],
  (get, set, setSelectedTeeth: string[]) => {
    get(selectedTeethAtom) === null
      ? set(selectedTeethAtom, [])
      : set(selectedTeethAtom, setSelectedTeeth);
  },
);

export const patientInfoAtom = atom(
  (get) => ({
    name: get(patientNameAtom),
    dob: get(patientDOBAtom),
    dateOfVisit: get(dateOfVisitAtom),
    radiographsTakenDate: get(radiographsTakenDateAtom),
    providerName: get(providerNameAtom),
  }),
  (get, set, newPatientInfo: TPatientInfo) => {
    set(patientNameAtom, newPatientInfo.name);
    set(patientDOBAtom, newPatientInfo.dob);
    set(dateOfVisitAtom, newPatientInfo.dateOfVisit);
    set(radiographsTakenDateAtom, newPatientInfo.radiographsTakenDate);
    set(providerNameAtom, newPatientInfo.providerName);
  },
);

export const isDialogOpenAtom = atom<boolean>(true);

// Clean claims attachment atoms - separate from the free claim summary atoms
export const cleanClaimsAttachmentUnifiedIdAtom = atom<string>('');
export const cleanClaimsAttachmentEstimatedVisitIdAtom = atom<string>('');
export const cleanClaimsAttachmentPmsAppointmentIdAtom = atom<
  string | undefined
>(undefined);
export const cleanClaimsAttachmentPatientInfoAtom = atom<
  Omit<TPatientInfo, 'radiographsTakenDate'>
>({
  name: '',
  dob: '',
  dateOfVisit: '',
  providerName: '',
});

// Clinical notes for clean claims attachment - scoped per appointment
const clinicalNotesMapAtom = atom<Map<string, string>>(new Map());
export const clinicalNotesAtom = atom(
  (get) => {
    const claimsAttachmentValues = get(cleanClaimsAttachmentValuesAtom);
    const unifiedId = claimsAttachmentValues.unifiedId;

    if (!unifiedId) return '';

    const notesMap = get(clinicalNotesMapAtom);
    return notesMap.get(unifiedId) || '';
  },
  (get, set, newValue: string) => {
    const claimsAttachmentValues = get(cleanClaimsAttachmentValuesAtom);
    const unifiedId = claimsAttachmentValues.unifiedId;

    if (!unifiedId) return;

    const notesMap = get(clinicalNotesMapAtom);
    const newMap = new Map(notesMap);
    newMap.set(unifiedId, newValue);
    set(clinicalNotesMapAtom, newMap);
  },
);

export const claimSummaryDialogAtom = atom(
  (get) => {
    return {
      view: get(claimSummaryViewAtom),
      estimatedVisitId: get(claimSummaryEstimatedVisitIdAtom),
      imagingPatientId: get(claimSummaryImagingPatientIdAtom),
      unifiedId: get(claimSummaryUnifiedIdAtom),
      appointmentPmsAppointmentId: get(appointmentPmsAppointmentIdAtom),
      appointmentStart: get(appointmentStartAtom),
      patientInfo: get(patientInfoAtom),
      insView: get(insPagesAtom),
      isOpen: get(isDialogOpenAtom),
    };
  },
  (
    _get,
    set,
    {
      unifiedId,
      estimatedVisitId,
      imagingPatientId,
      view,
      insView,
      appointment,
      patientInfo,
      isOpen,
      locale,
    }: {
      unifiedId: string;
      estimatedVisitId: string;
      imagingPatientId: string;
      view: TView;
      insView?: InsuranceView;
      appointment?: Appointment;
      patientInfo?: TPatientInfo;
      isOpen?: boolean;
      locale?: Locale;
    },
  ) => {
    if (!isOpen) {
      set(claimSummaryViewAtom, 'appointment');
      set(claimSummaryUnifiedIdAtom, '');
      set(claimSummaryImagingPatientIdAtom, '');
      set(claimSummaryEstimatedVisitIdAtom, '');
      return;
    }
    const dobFormatted =
      view === 'appointment'
        ? formatDate(appointment?.patient.dob || '', locale)
        : formatDate(patientInfo?.dob ?? '', locale);

    const visitDateFormatted =
      view === 'appointment'
        ? formatDate(appointment?.appointment.start || '', locale)
        : formatDate(patientInfo?.dateOfVisit ?? '', locale);

    const estimatedVisitIdFormatted =
      view === 'appointment'
        ? appointment?.appointment.estimatedVisitId?.toString() ?? ''
        : estimatedVisitId;

    const insViewFormatted =
      view === 'appointment' ? insView ?? 'config' : insView ?? 'preview-pages';

    const radiographsTakenDateFormatted =
      view === 'appointment'
        ? formatDate(appointment?.appointment.imagesAt || '', locale)
        : formatDate(patientInfo?.radiographsTakenDate ?? '', locale);

    const providerNameFormatted =
      view === 'appointment'
        ? getProviderName(appointment?.appointment.provider)
        : patientInfo?.providerName ?? '';

    const patientNameFormatted =
      view === 'appointment'
        ? appointment
          ? getDisplayName(appointment, 0).patientName
          : ''
        : patientInfo?.name ?? '';

    const imagingPatientIdFormatted =
      view === 'appointment'
        ? appointment?.patient.imagingPatientId
        : imagingPatientId;

    set(
      appointmentPmsAppointmentIdAtom,
      appointment?.appointment.pmsAppointmentId ?? undefined,
    );

    set(claimSummaryViewAtom, view);
    set(claimSummaryUnifiedIdAtom, unifiedId);
    set(claimSummaryEstimatedVisitIdAtom, estimatedVisitIdFormatted);
    set(insPagesAtom, insViewFormatted);
    set(claimSummaryImagingPatientIdAtom, imagingPatientIdFormatted);
    set(appointmentStartAtom, appointment?.appointment.start ?? undefined);

    set(patientInfoAtom, {
      name: patientNameFormatted,
      dob: dobFormatted,
      dateOfVisit: visitDateFormatted,
      radiographsTakenDate: radiographsTakenDateFormatted,
      providerName: providerNameFormatted,
    });
  },
);

// Clean claims attachment values atom - separate from the one in Huddle
export const cleanClaimsAttachmentValuesAtom = atom(
  (get) => {
    return {
      estimatedVisitId: get(cleanClaimsAttachmentEstimatedVisitIdAtom),
      unifiedId: get(cleanClaimsAttachmentUnifiedIdAtom),
      patientInfo: get(cleanClaimsAttachmentPatientInfoAtom),
      pmsAppointmentId: get(cleanClaimsAttachmentPmsAppointmentIdAtom),
    };
  },
  (
    _get,
    set,
    {
      unifiedId,
      estimatedVisitId,
      patientInfo,
      appointment,
      locale,
    }: {
      unifiedId: string;
      estimatedVisitId: string;
      patientInfo?: Omit<TPatientInfo, 'radiographsTakenDate'>;
      appointment?: Appointment;
      locale?: Locale;
    },
  ) => {
    const dobFormatted = patientInfo?.dob
      ? formatDate(patientInfo.dob, locale)
      : appointment?.patient.dob
      ? formatDate(appointment.patient.dob, locale)
      : '';

    const visitDateFormatted = patientInfo?.dateOfVisit
      ? formatDate(patientInfo.dateOfVisit, locale)
      : appointment?.appointment.start
      ? formatDate(appointment.appointment.start, locale)
      : '';

    const estimatedVisitIdFormatted =
      appointment?.appointment.estimatedVisitId?.toString() ?? estimatedVisitId;

    const providerNameFormatted =
      patientInfo?.providerName ??
      (appointment?.appointment.provider
        ? getProviderName(appointment.appointment.provider)
        : '');

    const patientNameFormatted =
      patientInfo?.name ??
      (appointment ? getDisplayName(appointment, 0).patientName : '');

    set(cleanClaimsAttachmentUnifiedIdAtom, unifiedId);
    set(cleanClaimsAttachmentEstimatedVisitIdAtom, estimatedVisitIdFormatted);
    set(
      cleanClaimsAttachmentPmsAppointmentIdAtom,
      appointment?.appointment.pmsAppointmentId ?? undefined,
    );

    set(cleanClaimsAttachmentPatientInfoAtom, {
      name: patientNameFormatted,
      dob: dobFormatted,
      dateOfVisit: visitDateFormatted,
      providerName: providerNameFormatted,
    });
  },
);

export type TImageSets = 'all' | 'selected';

export const selectedTreatmentsAtom = atom<string[] | undefined>(undefined);
export const selectedTreatmentCodesAtom = atom<string[]>([]);

export const includeFindingTypesAtom = atom<TIncludeTypes>({
  includeCariousLesions: false,
  includePeriapicalRadiolucency: false,
  includeRadiographicBoneLoss: false,
  includeDmf: false,
  includeRadiographsTakenDate: false,
  includeBitewingsOnly: false,
});

export const includeFindingTypeAtom = atom(
  (get) => get(includeFindingTypesAtom),
  (get, set, type: keyof TIncludeTypes, value: boolean) => {
    set(includeFindingTypesAtom, {
      ...get(includeFindingTypesAtom),
      [type]: value,
    });
  },
);

export const imageSetsAtom = atom<TImageSets>('selected');
export const claimNotesAtom = atom<string[]>([]);

// Atoms for Print Version PDF generation
export const claimAttachmentToothLevelImagesAtom = atom<
  Array<{
    title: string;
    imageId: number;
    annotations: AnnotationsProps;
    tableData: Array<{
      tooth: string;
      txCodes: string[];
      dmf: string;
      caries: boolean;
      resto: boolean;
    }>;
  }>
>([]);

export const claimAttachmentFmxImagesAtom = atom<
  Array<{
    imageId: number;
    annotations: AnnotationsProps;
  } | null>
>([]);

export const claimAttachmentFmxTableDataAtom = atom<
  Array<{
    tooth: string;
    txCodes: string[];
    calculus: boolean;
    rblMesial: string | null;
    rblDistal: string | null;
  }>
>([]);
